import { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { animated, useSpring, useTransition } from "react-spring";
import Skeleton from "react-loading-skeleton";
import ReactSelect from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCheckSquare,
  faCircleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  FieldError,
  InputGroup,
  InputSkeletonContainer,
  Label,
} from "core/components/Form/styles";
import Chip from "core/components/Chip";
import Typography from "core/components/Typography";
import { Tooltip } from "react-tooltip";
import {
  StyledControl,
  StyledPlaceholder,
  StyledInput,
  StyledNoOptionsMessage,
  StyledSingleValue,
  StyledIndicatorsContainer,
  StyledDropdownIndicator,
  StyledClearIndicator,
  StyledIndicatorSeparator,
  StyledMenu,
  MenuControls,
  StyledGroup,
  StyledGroupHeading,
  StyledMenuList,
  StyledOption,
  EmptyCheckbox,
  StyledValueContainer,
  AnimatedMultiValueList,
  SecondaryButton,
} from "core/components/Field/Combobox/styles";

const CustomInput = (props) => {
  // Fix for ReactSelect bug: https://github.com/JedWatson/react-select/issues/3923
  // When openMenuOnClick={false}, the input opacity stays at 0 after selection.
  // React-select sets isHidden: true which causes opacity: 0 inline style.
  // Override isHidden: false to keep the input visible.
  return (
    <StyledInput
      {...props}
      isHidden={false}
      key="input"
      autoComplete={props.selectProps?.autoComplete ?? props.autoComplete}
    >
      {props.children}
    </StyledInput>
  );
};

const CustomNoOptionsMessage = (props) => {
  const { NoOptionsComponent } = props.selectProps;

  if (NoOptionsComponent) {
    return (
      <NoOptionsComponent
        {...props}
        ContainerComponent={StyledNoOptionsMessage}
      />
    );
  }

  return (
    <StyledNoOptionsMessage {...props}>
      <Typography variant="body" noMargin style={{ fontWeight: "700" }}>
        No matches found
      </Typography>
    </StyledNoOptionsMessage>
  );
};

const CustomIndicatorsContainer = (props) => {
  return (
    <StyledIndicatorsContainer {...props}>
      {props.selectProps.errorMessage && (
        <FontAwesomeIcon
          icon={props.selectProps?.icons?.hasError ?? faCircleExclamation}
          style={{ marginRight: "12px", marginTop: "2px", color: "#BA351C" }}
        />
      )}
      {props.children}
    </StyledIndicatorsContainer>
  );
};

const CustomDropdownIndicator = (props) => {
  return (
    <StyledDropdownIndicator {...props}>
      <FontAwesomeIcon
        icon={props.selectProps?.icons?.openMenu ?? faCaretDown}
        style={{ marginBottom: "1px" }}
      />
    </StyledDropdownIndicator>
  );
};

const CustomClearIndicator = (props) => {
  return (
    <StyledClearIndicator
      {...props}
      innerProps={Object.assign({}, props.innerProps, {
        "data-test": "select-clear-button",
      })}
    >
      <FontAwesomeIcon
        icon={props.selectProps?.icons?.clearValues ?? faXmark}
      />
    </StyledClearIndicator>
  );
};

const CustomMenuList = (props) => {
  return <StyledMenuList {...props}>{props.children}</StyledMenuList>;
};

const CustomStyledMenu = (props) => {
  const { menuControls } = props.selectProps;

  return (
    <StyledMenu
      {...props}
      innerProps={Object.assign({}, props.innerProps, {
        "data-test": "react-select-menu-testid",
      })}
    >
      {menuControls && (
        <MenuControls
          onMouseDown={(event) => {
            // Keeps focus on the select's input — otherwise the click blurs it
            // and react-select closes the menu.
            event.preventDefault();
            event.stopPropagation();
          }}
          onTouchEnd={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {menuControls}
        </MenuControls>
      )}
      {props.children}
    </StyledMenu>
  );
};

const CustomOption = (props) => {
  const {
    data,
    label,
    innerProps,
    isDisabled,
    isFocused,
    isSelected: baseIsSelected,
    isMulti,
    selectProps,
  } = props;

  const { isAllSelected, isGrouped, onGroupClick, value, icons } = selectProps;
  const isSelectAll = isMulti && data.value === "__ALL";
  const isGroup = !!data.children;
  const isSelected = isSelectAll
    ? isAllSelected
    : isGroup
    ? data.children.every(
        (option) =>
          Array.isArray(value) &&
          !!value.find((item) => item.value === option.value)
      )
    : baseIsSelected;

  // No icon for single-select: a tick only restates what the purple row says,
  // and costs every option an icon-width indent. Multi-select keeps its
  // checkboxes, which are a control rather than a marker.
  const selectedIcon = icons?.isSelectedMulti ?? faCheckSquare;
  const hasIcon = isMulti && isSelected;

  const baseProps = {
    ...innerProps,
    isMulti,
    isDisabled,
    isFocused,
    isSelected,
    isGroupOption: isGroup && !isSelectAll,
    isChildOption: !isGroup && !isSelectAll && isGrouped,
    onClick: isSelectAll
      ? data.onClick
      : isGroup
      ? isMulti
        ? () => onGroupClick(data.groupId, isSelected)
        : null
      : innerProps.onClick,
    selectProps,
  };

  return (
    <StyledOption {...baseProps}>
      {hasIcon && <FontAwesomeIcon icon={selectedIcon} />}
      {!hasIcon && isMulti && <EmptyCheckbox />}
      <div
        data-option-value={data.value}
        data-tooltip-id={data.value}
        style={{
          marginTop: isSelected ? 1 : "unset",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {isDisabled ? (
          <>
            <span style={{ opacity: 0.5 }}>{label}</span>
            <Tooltip
              id={data.value}
              positionStrategy="fixed"
              place="right-end"
              style={{ zIndex: 100000 }}
              classNameArrow="tooltip-arrow"
              offset={8}
            >
              {data?.hintText}
            </Tooltip>
          </>
        ) : (
          <span>{label}</span>
        )}
      </div>
    </StyledOption>
  );
};

const CustomMultiValue = (props) => {
  return (
    <Chip
      text={props.data.label}
      variant="actionable"
      onDismiss={props.removeProps.onClick}
      size={props.selectProps.size}
    />
  );
};

const CustomValueContainer = (props) => {
  const { children, isMulti, options, ...rest } = props;
  const { multiValueMaxDisplayCount, size } = rest.selectProps;
  const [value, input] = children;
  const isValuePlaceholder = !!value && value?.key === "placeholder";
  const isValueSelectedItems = value && !isValuePlaceholder;
  const valuesExceedMax =
    isValueSelectedItems && value?.length > multiValueMaxDisplayCount;

  const [isValueListExpanded, setIsValueListExpanded] = useState(false);

  const expandList = () => {
    setIsValueListExpanded(true);
  };

  const collapseList = () => {
    setIsValueListExpanded(false);
  };

  const visibleValues = useMemo(() => {
    if (isValueSelectedItems && valuesExceedMax && !isValueListExpanded) {
      return value.filter((_, index) => index < multiValueMaxDisplayCount);
    }

    return isValueSelectedItems ? value : [];
  }, [
    isValueSelectedItems,
    valuesExceedMax,
    isValueListExpanded,
    value,
    multiValueMaxDisplayCount,
  ]);

  const containerStyle = useSpring({
    height: "auto",
    config: { duration: 0 },
  });

  const transitions = useTransition(visibleValues, {
    from: { opacity: 1 },
    enter: { opacity: 1 },
    leave: {
      opacity: 0,
      delay: 0,
      config: { duration: 0 },
    },
    keys: (_, index) => index,
  });

  return (
    <StyledValueContainer {...rest} isMulti={isMulti}>
      {isMulti && (
        <animated.div style={containerStyle}>
          <AnimatedMultiValueList size={size}>
            {isValueSelectedItems ? (
              <>
                {transitions((style, item) => (
                  <animated.div key={item.key} style={style}>
                    {item}
                  </animated.div>
                ))}
                {valuesExceedMax && (
                  <SecondaryButton
                    onClick={isValueListExpanded ? collapseList : expandList}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    {isValueListExpanded ? "See Less" : "See More"}
                  </SecondaryButton>
                )}
              </>
            ) : (
              value
            )}
            {input}
          </AnimatedMultiValueList>
        </animated.div>
      )}
      {!isMulti && children}
    </StyledValueContainer>
  );
};

const areOptionsGrouped = (options) => options.some((o) => !!o.children);

const getAllGroupedOptions = (options) =>
  options.reduce((list, group) => {
    group.children.forEach((option) => list.push(option));
    return list;
  }, []);

const getAllGroupedOptionsByGroupId = (options, groupId) =>
  options.find((group) => group.groupId === groupId).children;

const areAllOptionsSelected = (options, value) => {
  if (areOptionsGrouped(options)) {
    const groupedOptions = getAllGroupedOptions(options);
    return Array.isArray(value) && groupedOptions.length === value.length;
  }
  return Array.isArray(value) && options.length === value.length;
};

const DEFAULT_ICONS = {
  openMenu: faCaretDown,
  isSelectedMulti: faCheckSquare,
  hasError: faCircleExclamation,
  clearValues: faXmark,
};

const Combobox = ({
  id = "",
  name = "",
  label = "",
  placeholder = "",
  size = "large",
  options = [],
  isSearchable = false,
  isMultiSelect = false,
  isLoading = false,
  isDisabled = false,
  // Left undefined on purpose: react-select then applies its own default
  // (clearable when multi-select). Field callers go through ComboboxWrapper,
  // which derives it from isRequired.
  isClearable,
  autoFocus = false,
  onChange = null,
  multiValueMaxDisplayCount = 10,
  value = "",
  error = "",
  errorMessage = "",
  hideBuiltInFieldError = false,
  icons = DEFAULT_ICONS,
  NoOptionsComponent,
  openMenuOnClick = true,
  filterOption,
  // Rendered at the top of the open menu: filters, segmented controls,
  // anything that narrows what is listed below.
  menuControls,
  // A `menuControls` filter usually wants resetting once the menu closes.
  onMenuOpen,
  onMenuClose,
  formatOptionLabel,
  getOptionLabel,
  autoComplete,
  dataTest,
}) => {
  const resolvedError = error || errorMessage;
  const _icons = useMemo(() => ({ ...DEFAULT_ICONS, ...icons }), [icons]);

  const [isGrouped] = useState(areOptionsGrouped(options));
  const [isAllSelected, setIsAllSelected] = useState(
    areAllOptionsSelected(options, value)
  );

  const handleSelectChange = useCallback(
    (newValue) => {
      if (isMultiSelect) {
        const allOptionsSelected = areAllOptionsSelected(options, newValue);
        setIsAllSelected(allOptionsSelected);
        onChange(newValue.map((option) => option.value));
      } else {
        onChange(newValue?.value ?? "");
      }
    },
    [isMultiSelect, onChange, options]
  );

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      handleSelectChange([]);
    } else {
      handleSelectChange(isGrouped ? getAllGroupedOptions(options) : options);
    }
  }, [isAllSelected, options, isGrouped, handleSelectChange]);

  const preparedOptions = useMemo(() => {
    const selectAllOption = {
      label: "Select All",
      value: "__ALL",
      onClick: toggleSelectAll,
    };
    const selectTypeOptions = isMultiSelect
      ? [selectAllOption].concat(options)
      : options;

    return selectTypeOptions.reduce((list, optionOrGroup) => {
      const children = optionOrGroup.children || [];
      return list.concat(optionOrGroup).concat(children);
    }, []);
  }, [isMultiSelect, options, toggleSelectAll]);

  // Value lookup needs a flat list including grouped members. Kept separate
  // from `preparedOptions`, which react-select renders — flattening there would
  // draw every grouped option a second time at the top level.
  const optionsForValueLookup = useMemo(
    () =>
      preparedOptions.reduce(
        (list, optionOrGroup) =>
          list.concat(optionOrGroup).concat(optionOrGroup.options ?? []),
        []
      ),
    [preparedOptions]
  );

  // Holds the last resolved option so a selection stays displayed when the
  // caller narrows `options` and filters it out of the menu.
  const lastResolvedOptionRef = useRef(null);

  const preparedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return optionsForValueLookup.filter((option) =>
        value.includes(option.value)
      );
    }

    const match = optionsForValueLookup.find(
      (option) => option.value === value
    );

    if (match) {
      lastResolvedOptionRef.current = match;
      return match;
    }

    return lastResolvedOptionRef.current?.value === value
      ? lastResolvedOptionRef.current
      : "";
  }, [value, optionsForValueLookup]);

  const toggleGroup = (groupId, isSelected) => {
    const childOptions = getAllGroupedOptionsByGroupId(options, groupId);

    if (isSelected) {
      const filteredValues = value
        .filter(
          (valueAsString) =>
            !childOptions.find((o) => o.value === valueAsString)
        )
        .map((v) => preparedValue.find((p) => p.value === v));

      handleSelectChange(filteredValues);
    } else {
      const missingValues = childOptions.filter((option) => {
        if (Array.isArray(preparedValue)) {
          return !preparedValue.find((p) => p.value === option.value);
        }
        return true;
      });

      handleSelectChange(
        Array.isArray(preparedValue)
          ? preparedValue.concat(missingValues)
          : missingValues
      );
    }
  };

  if (isLoading) {
    return (
      <InputGroup>
        {label && <Label>{label}</Label>}
        <InputSkeletonContainer size={size} type="combobox">
          <Skeleton />
        </InputSkeletonContainer>
      </InputGroup>
    );
  }

  return (
    <InputGroup disabled={isDisabled} data-test={dataTest}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <ReactSelect
        classNamePrefix="react-select"
        openMenuOnFocus={false}
        openMenuOnClick={openMenuOnClick}
        inputId={id}
        name={name}
        options={preparedOptions}
        value={preparedValue}
        onChange={handleSelectChange}
        autoFocus={autoFocus}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isMulti={isMultiSelect}
        hideSelectedOptions={false}
        closeMenuOnSelect={!isMultiSelect}
        isClearable={isClearable}
        formatOptionLabel={formatOptionLabel}
        getOptionLabel={getOptionLabel}
        filterOption={filterOption}
        size={size}
        errorMessage={resolvedError}
        multiValueMaxDisplayCount={multiValueMaxDisplayCount}
        isAllSelected={isAllSelected}
        isGrouped={isGrouped}
        onGroupClick={toggleGroup}
        NoOptionsComponent={NoOptionsComponent}
        menuControls={menuControls}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        icons={_icons}
        autoComplete={autoComplete}
        unstyled={true}
        components={{
          Control: StyledControl,
          Placeholder: StyledPlaceholder,
          Input: CustomInput,
          SingleValue: StyledSingleValue,
          Menu: CustomStyledMenu,
          MenuList: CustomMenuList,
          Group: StyledGroup,
          GroupHeading: StyledGroupHeading,
          IndicatorSeparator: StyledIndicatorSeparator,
          NoOptionsMessage: CustomNoOptionsMessage,
          IndicatorsContainer: CustomIndicatorsContainer,
          DropdownIndicator: CustomDropdownIndicator,
          ClearIndicator: CustomClearIndicator,
          ValueContainer: CustomValueContainer,
          MultiValue: CustomMultiValue,
          Option: CustomOption,
        }}
      />
      {!hideBuiltInFieldError && size !== "small" && (
        <FieldError>{resolvedError}</FieldError>
      )}
    </InputGroup>
  );
};

Combobox.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(["small", "large"]),
  error: PropTypes.string,
  errorMessage: PropTypes.string,
  isSearchable: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hideBuiltInFieldError: PropTypes.bool,
  multiValueMaxDisplayCount: PropTypes.number,
  icons: PropTypes.shape({
    openMenu: PropTypes.object,
    isSelectedMulti: PropTypes.object,
    hasError: PropTypes.object,
    clearValues: PropTypes.object,
  }),
  NoOptionsComponent: PropTypes.elementType,
  menuControls: PropTypes.node,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  openMenuOnClick: PropTypes.bool,
  filterOption: PropTypes.func,
  formatOptionLabel: PropTypes.func,
  getOptionLabel: PropTypes.func,
  autoComplete: PropTypes.string,
  dataTest: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
      groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      children: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

export default Combobox;
